import { useState } from "react";
import { Ghost, X } from "lucide-react";
import { CHILDHOOD, QUESTS } from "@/data/misc";
import { InfoNote } from "@/components/InfoNote";
import { useAchievements } from "@/os/state/achievements";
import { play } from "@/lib/sound";

const SAX_IMG =
    "https://images.unsplash.com/photo-1581283869631-baae04233b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const FOOD_IMG =
    "https://images.unsplash.com/photo-1526318896980-cf78c088247c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const cardCls = "overflow-hidden rounded-2xl border-2 border-ink bg-paper shadow-[3px_3px_0_#1C1917]";

export function OutsideTerminal() {
    const [showHidden, setShowHidden] = useState(false);
    const [saxOpen, setSaxOpen] = useState(false);
    const { trackEgg, unlock } = useAchievements();

    return (
        <div className="relative" data-testid="outside-terminal">
            <div className="grid gap-3 sm:grid-cols-2">
                <div className={cardCls}>
                    <img src={SAX_IMG} alt="A saxophone" className="h-32 w-full object-cover" loading="lazy" />
                    <div className="p-4">
                        <p className="font-display text-sm font-extrabold">the saxophone</p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-inksoft">
                            i play the saxophone. long notes are hard, vibrato is harder, and the reed has opinions.
                        </p>
                    </div>
                </div>

                <div className={cardCls}>
                    <img src={FOOD_IMG} alt="A bowl of noodles" className="h-32 w-full object-cover" loading="lazy" />
                    <div className="p-4">
                        <p className="font-display text-sm font-extrabold">food & mukbang</p>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-inksoft">
                            i enjoy food and watching mukbang videos. i prefer eating to cooking — appreciation is also a skill.
                        </p>
                    </div>
                </div>

                <div className={`${cardCls} p-4 sm:col-span-2`}>
                    <p className="font-display text-sm font-extrabold">the childhood curriculum</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {CHILDHOOD.map((c) => (
                            <span key={c} className="chip" data-testid="childhood-chip">
                                {c}
                            </span>
                        ))}
                    </div>
                    <p className="mt-2 font-mono text-[10.5px] text-inkfaint">status: completed. rewards kept: all of them.</p>
                </div>

                <div className={`${cardCls} p-4`}>
                    <p className="font-display text-sm font-extrabold">toastmasters</p>
                    <p className="mb-2 mt-1 text-[12.5px] leading-relaxed text-inksoft">
                        developing public speaking skills, one evaluated speech at a time.
                    </p>
                    <InfoNote
                        text="structure beats confidence. prepared speeches are the easy part — table topics (a subject and two minutes, no preparation) are where the real debugging happens, and the evaluation afterwards is the whole point: honest feedback, every session, nowhere to hide."
                        testId="toastmasters"
                    />
                </div>

                <div className="rounded-2xl border-2 border-dashed border-inkfaint bg-sandsoft/40 p-4">
                    <p className="font-display text-sm font-extrabold">hidden files</p>
                    <p className="mt-1 font-mono text-[10.5px] text-inkfaint">this folder contains 1 hidden file. it's not hiding well.</p>
                    <button
                        data-testid="show-hidden-files"
                        className="btn btn-ghost btn-sm mt-2"
                        aria-expanded={showHidden}
                        onClick={() => {
                            setShowHidden((v) => !v);
                            play("click");
                        }}
                    >
                        {showHidden ? "hide hidden files" : "show hidden files (1)"}
                    </button>
                    {showHidden && (
                        <button
                            data-testid="hidden-file"
                            className="file-row mt-2"
                            onClick={() => {
                                setSaxOpen(true);
                                trackEgg("egg_sax_file");
                                unlock("beyond_keyboard");
                            }}
                        >
                            <Ghost size={16} className="flex-none text-lav" />
                            <span className="truncate">:something_not_written_in_python.md3</span>
                        </button>
                    )}
                </div>
            </div>

            <h3 className="mt-6 font-display text-lg font-extrabold">side quests</h3>
            <p className="font-mono text-[11px] text-inkfaint">
                life outside the editor, presented as XP — because everything is a side quest if you commit hard enough.
            </p>
            <div className="mt-3 space-y-3">
                {QUESTS.map((q) => (
                    <div key={q.title} className="rounded-2xl border-2 border-ink bg-paper p-4 shadow-[3px_3px_0_#1C1917]" data-testid="quest-card">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-mono text-[12px] font-bold">{q.title}</p>
                            <span className="badge" style={{ borderColor: q.color, color: q.color }}>
                                {q.status}
                            </span>
                        </div>
                        <div className="xp-track mt-2">
                            <div className="xp-fill" style={{ width: `${q.xp}%`, background: q.color }} />
                        </div>
                        <p className="mt-2 text-[12.5px] leading-relaxed text-inksoft">{q.desc}</p>
                        {q.note && (
                            <div className="mt-2">
                                <InfoNote text={q.note} testId={`quest-${q.title.replace(/[^a-z]+/g, "-")}-note`} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {saxOpen && (
                <div className="absolute inset-0 z-30 grid place-items-center rounded-2xl bg-ink/30 p-3" data-testid="sax-modal">
                    <div className="pop-in max-h-full w-full overflow-auto os-scroll rounded-2xl border-2 border-ink bg-paper p-4 shadow-[5px_5px_0_#1C1917]">
                        <div className="flex items-center gap-2">
                            <Ghost size={15} className="flex-none text-lav" />
                            <p className="flex-1 truncate font-mono text-[12px] font-bold">:something_not_written_in_python.md3</p>
                            <button data-testid="sax-modal-close" aria-label="Close" className="text-inkfaint hover:text-ink" onClick={() => setSaxOpen(false)}>
                                <X size={15} />
                            </button>
                        </div>
                        <p className="mt-2 text-[13px] leading-relaxed text-inksoft">
                            file type: not python. status: practicing. error rate: high. spirit: uncompromising.
                        </p>
                        <p className="mt-2 text-[13px] leading-relaxed text-inksoft">
                            the saxophone doesn't do stack traces. it just sounds off, and you fix it by ear — debugging with your
                            soul, one long note at a time.
                        </p>
                        <div className="mt-3">
                            <InfoNote
                                text="the saxophone wasn't my first instrument — carnatic music came first, and bharatanatyam before that, so rhythm arrived long before reeds did. the sax was the first one i picked entirely because i wanted to, which makes practising it feel less like a curriculum and more like a side quest. favourite thing to play: anything slow enough that a long, clean note is the whole achievement."
                                testId="sax-story"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
